/**
 * Display formatting helpers. All functions are pure and accept an optional
 * locale/timeZone so they can be tested deterministically.
 */

const DASH = '—';

/** 3725 -> "1h 02m", 754 -> "12m 34s", 42 -> "42s". */
export function formatDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return DASH;
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(r).padStart(2, '0')}s`;
  return `${r}s`;
}

/** Offset from the start of a call: 65_000 -> "01:05", 3_725_000 -> "1:02:05". */
export function formatOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export interface LocaleOptions {
  locale?: string;
  timeZone?: string;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  // Bare calendar days ("2026-09-21") are local dates, not UTC midnight.
  const day = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  const d = value instanceof Date ? value : day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Sep 24, 2026" */
export function formatDate(value: string | Date | null | undefined, opts: LocaleOptions = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  return new Intl.DateTimeFormat(opts.locale, { dateStyle: 'medium', timeZone: opts.timeZone }).format(d);
}

/** "Sep 24, 2026, 2:05 PM" */
export function formatDateTime(value: string | Date | null | undefined, opts: LocaleOptions = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  return new Intl.DateTimeFormat(opts.locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: opts.timeZone,
  }).format(d);
}

/** "2:05 PM" */
export function formatTime(value: string | Date | null | undefined, opts: LocaleOptions = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  return new Intl.DateTimeFormat(opts.locale, { timeStyle: 'short', timeZone: opts.timeZone }).format(d);
}

/** "$1,234.50" (always 2 decimals). */
export function formatUsd(value: number | null | undefined, locale?: string): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

/** "12,345" */
export function formatNumber(value: number | null | undefined, locale?: string): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/** "12.9K", "4.2M" for large standalone figures. */
export function formatCompact(value: number | null | undefined, locale?: string): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** Ratio 0..1 -> "62%". */
export function formatPercent(ratio: number | null | undefined, locale?: string): string {
  if (ratio == null || !Number.isFinite(ratio)) return DASH;
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(ratio);
}

/** Share of talk time spoken by the rep ("me"), or null when there was no speech. */
export function talkRatioMe(talk: { meSec: number; themSec: number } | null | undefined): number | null {
  if (!talk) return null;
  const total = talk.meSec + talk.themSec;
  return total > 0 ? talk.meSec / total : null;
}

/** Share of positive tip feedback, or null when there is none. */
export function positiveShare(up: number, down: number): number | null {
  const total = up + down;
  return total > 0 ? up / total : null;
}

/** "Sep 24" */
export function formatShortDate(value: string | Date | null | undefined, opts: LocaleOptions = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  return new Intl.DateTimeFormat(opts.locale, { month: 'short', day: 'numeric', timeZone: opts.timeZone }).format(d);
}
